import { fabric } from 'fabric';
import { Bounds, getSlideBounds } from '../../../utils/objectGeometry';
import {
  getGuideTargets,
  getSmartSnap,
  Guide,
} from '../../../utils/smartGuides';

// Screen pixels, so snapping feels the same at every zoom.
const SNAP_DISTANCE = 8;
const GUIDE_COLOR = '#e0379b';
const SNAP_SETTING_KEY = 'canvasEditor.snapToGuides';

const readSnapSetting = () => {
  try {
    return localStorage.getItem(SNAP_SETTING_KEY) !== 'off';
  } catch {
    return true;
  }
};

let snapEnabled = readSnapSetting();

export const isSnapEnabled = () => snapEnabled;

export const setSnapEnabled = (enabled: boolean) => {
  snapEnabled = enabled;
  try {
    localStorage.setItem(SNAP_SETTING_KEY, enabled ? 'on' : 'off');
  } catch {
    // Not remembered; the setting still applies to this session.
  }
};

const guidesByCanvas = new WeakMap<fabric.Canvas, Guide[]>();

const getSlideArea = (canvas: fabric.Canvas): Bounds => {
  const zoom = canvas.getZoom() || 1;
  return {
    left: 0,
    top: 0,
    width: canvas.getWidth() / zoom,
    height: canvas.getHeight() / zoom,
  };
};

const snap = (
  canvas: fabric.Canvas,
  bounds: Bounds,
  moving: fabric.Object[],
) => {
  const others = canvas
    .getObjects()
    .filter((object) => object.visible !== false && !moving.includes(object))
    .map(getSlideBounds);
  const result = getSmartSnap(
    bounds,
    getGuideTargets(getSlideArea(canvas), others),
    SNAP_DISTANCE / (canvas.getZoom() || 1),
  );
  guidesByCanvas.set(canvas, result.guides);
  return result.offset;
};

// Called while an object (or multi-selection) is dragged on the canvas.
export const snapMovingObject = (
  canvas: fabric.Canvas,
  target: fabric.Object,
) => {
  if (!snapEnabled) return;
  const moving =
    target.type === 'activeSelection'
      ? (target as fabric.ActiveSelection).getObjects()
      : [target];
  const offset = snap(canvas, getSlideBounds(target), moving);
  if (!offset.x && !offset.y) return;
  target.set({
    left: (target.left || 0) + offset.x,
    top: (target.top || 0) + offset.y,
  });
  target.setCoords();
};

// Widgets are dragged through their HTML overlay; positions are in slide
// coordinates.
export const snapWidgetPosition = (
  canvas: fabric.Canvas,
  object: fabric.Object,
  position: { left: number; top: number },
) => {
  if (!snapEnabled) return position;
  const offset = snap(
    canvas,
    {
      ...position,
      width: object.getScaledWidth(),
      height: object.getScaledHeight(),
    },
    [object],
  );
  return { left: position.left + offset.x, top: position.top + offset.y };
};

export const clearGuides = (canvas: fabric.Canvas) => {
  const hadGuides = !!guidesByCanvas.get(canvas)?.length;
  guidesByCanvas.delete(canvas);
  if (hadGuides) canvas.requestRenderAll();
};

// Drawn on the top canvas after every render; Fabric clears it on the next
// render because it is marked dirty.
export const drawGuides = (canvas: fabric.Canvas) => {
  const guides = guidesByCanvas.get(canvas);
  if (!guides?.length) return;
  const ctx = canvas.getSelectionContext();
  const transform = canvas.viewportTransform;
  const slide = getSlideArea(canvas);
  ctx.save();
  if (transform) {
    ctx.transform(
      transform[0],
      transform[1],
      transform[2],
      transform[3],
      transform[4],
      transform[5],
    );
  }
  ctx.strokeStyle = GUIDE_COLOR;
  ctx.lineWidth = 1 / (canvas.getZoom() || 1);
  ctx.beginPath();
  guides.forEach(({ orientation, position }) => {
    if (orientation === 'vertical') {
      ctx.moveTo(position, slide.top);
      ctx.lineTo(position, slide.top + slide.height);
    } else {
      ctx.moveTo(slide.left, position);
      ctx.lineTo(slide.left + slide.width, position);
    }
  });
  ctx.stroke();
  ctx.restore();
  // @ts-ignore: not in the type definitions.
  canvas.contextTopDirty = true;
};
